// src/fileUtils/pdfUtils.js - Updated to better handle image-based PDFs
import * as pdfjs from 'pdfjs-dist';

// Initialize PDF.js worker - keep this simpler and more reliable
try {
  // Try to set the PDF.js worker source to a reliable CDN
  const pdfjsVersion = pdfjs.version || '3.4.120';
  pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsVersion}/pdf.worker.min.js`;
} catch (err) {
  console.error("Failed to set PDF.js worker source:", err);
  // Silent failure - will fall back to browser built-in PDF capabilities
}

/**
 * Extract text content from a PDF file
 * @param {string|ArrayBuffer} pdfData - PDF data as dataURL or buffer
 * @param {Object} options - Options for text extraction
 * @returns {Promise<string>} - Extracted text content
 */
async function extractTextFromPDF(pdfData, options = {}) {
  const {
    startPage = 1,
    endPage = null,  // null means all pages
    timeout = 30000, // 30 seconds timeout
    preserveFormatting = true,
    ignoreErrors = true
  } = options;
  
  // Timeout for text extraction
  const timeoutPromise = new Promise((_, reject) => {
    setTimeout(() => reject(new Error("PDF text extraction timed out")), timeout);
  });
  
  try {
    console.log("Starting PDF text extraction");
    
    // Process PDF data into format PDF.js can use
    let source;
    if (typeof pdfData === 'string' && pdfData.startsWith('data:')) {
      try {
        console.log("Processing data URL for text extraction");
        const base64 = pdfData.split(',')[1];
        const binary = atob(base64);
        const len = binary.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = binary.charCodeAt(i);
        }
        source = { data: bytes.buffer };
      } catch (err) {
        console.error("Error decoding base64 data URL:", err);
        throw new Error("Failed to decode PDF data URL");
      }
    } else if (pdfData instanceof ArrayBuffer || pdfData instanceof Uint8Array) {
      console.log("Processing ArrayBuffer for text extraction");
      source = { data: pdfData };
    } else {
      console.error("Unsupported PDF data format for text extraction");
      throw new Error("Unsupported PDF data format");
    }
    
    // Configure PDF.js to load the document
    const loadingTask = pdfjs.getDocument({
      ...source,
      disableFontFace: false,
      cMapUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@latest/cmaps/',
      cMapPacked: true,
      verbosity: 1,
      standardFontDataUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@latest/standard_fonts/',
      ignoreErrors: ignoreErrors,
      isEvalSupported: true,
      useSystemFonts: true,
      enableXfa: true
    });
    
    console.log("PDF.js loading task created for text extraction");
    
    // Race against timeout
    const pdf = await Promise.race([loadingTask.promise, timeoutPromise]);
    console.log("PDF document loaded for text extraction, pages:", pdf.numPages);
    
    // Determine the range of pages to extract
    const pdfStartPage = Math.max(1, startPage);
    const pdfEndPage = endPage ? Math.min(endPage, pdf.numPages) : pdf.numPages;
    
    // Extract text from each page
    let fullText = '';
    
    for (let pageNum = pdfStartPage; pageNum <= pdfEndPage; pageNum++) {
      try {
        console.log(`Extracting text from page ${pageNum}`);
        const page = await pdf.getPage(pageNum);
        const textContent = await page.getTextContent();
        
        if (preserveFormatting) {
          // Group text items by line and preserve some formatting
          const textItems = textContent.items;
          let lastY;
          let lineText = '';
          
          for (let i = 0; i < textItems.length; i++) {
            const item = textItems[i];
            
            // Check if we're on a new line based on y-position
            if (lastY !== undefined && Math.abs(lastY - item.transform[5]) > 5) {
              fullText += lineText + '\n';
              lineText = '';
            }
            
            lineText += item.str;
            
            // If the next item is far away horizontally, add a space
            if (i + 1 < textItems.length) {
              const nextItem = textItems[i + 1];
              const xGap = nextItem.transform[4] - (item.transform[4] + item.width);
              if (xGap > 10) {
                lineText += ' ';
              }
            }
            
            lastY = item.transform[5];
          }
          
          // Add the last line
          if (lineText.trim().length > 0) {
            fullText += lineText + '\n';
          }
        } else {
          // Simple text extraction without formatting
          const strings = textContent.items.map(item => item.str);
          fullText += strings.join(' ') + '\n\n';
        }
        
        // Add a page separator if not the last page
        if (pageNum < pdfEndPage) {
          fullText += preserveFormatting ? '\n\n--- Page ' + pageNum + ' ---\n\n' : '\n\n';
        }
      } catch (pageErr) {
        console.error(`Error extracting text from page ${pageNum}:`, pageErr);
        if (!ignoreErrors) {
          throw pageErr;
        }
        fullText += `\n[Error extracting text from page ${pageNum}]\n`;
      }
    }
    
    console.log("PDF text extraction complete");
    return fullText.trim();
  } catch (err) {
    console.error("Error extracting text from PDF:", err);
    throw new Error(`Failed to extract text from PDF: ${err.message}`);
  }
}

/**
 * Generate a PDF thumbnail with improved image-only PDF handling
 * @param {string|ArrayBuffer} pdfData - PDF data as dataURL or buffer
 * @param {Object} options - Options for thumbnail generation
 * @returns {Promise<string>} - Thumbnail data URL
 */
async function generatePDFThumbnail(pdfData, options = {}) {
  const { 
    page = 1, 
    width = 800, 
    height = 1200,
    useHighQualityRendering = true, // Enable high quality by default for image PDFs
    scale = 1.5, // Higher scale for better quality with image PDFs
    ignoreErrors = true // Continue even if there are non-fatal errors
  } = options;
  
  // Longer timeout for thumbnail generation - increased for image-based PDFs
  const timeoutPromise = new Promise((_, reject) => {
    setTimeout(() => reject(new Error("PDF thumbnail generation timed out")), 20000);
  });
  
  // First create a basic placeholder thumbnail that will display immediately
  const placeholderThumbnail = generatePlaceholderThumbnail("Loading PDF Preview...");
  
  try {
    console.log("Starting enhanced PDF thumbnail generation");
    
    // Process PDF data into format PDF.js can use
    let source;
    if (typeof pdfData === 'string' && pdfData.startsWith('data:')) {
      try {
        console.log("Processing data URL for thumbnail");
        const base64 = pdfData.split(',')[1];
        const binary = atob(base64);
        const len = binary.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = binary.charCodeAt(i);
        }
        source = { data: bytes.buffer };
      } catch (err) {
        console.error("Error decoding base64 data URL:", err);
        return generatePdfErrorThumbnail("PDF Preview Unavailable");
      }
    } else if (pdfData instanceof ArrayBuffer || pdfData instanceof Uint8Array) {
      console.log("Processing ArrayBuffer for thumbnail");
      source = { data: pdfData };
    } else {
      console.error("Unsupported PDF data format for thumbnail");
      return generatePdfErrorThumbnail("PDF Preview Unavailable");
    }
    
    // Use a more robust configuration for PDF.js specifically for image-based PDFs
    const loadingTask = pdfjs.getDocument({
      ...source,
      disableFontFace: false,
      cMapUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@latest/cmaps/',
      cMapPacked: true,
      verbosity: 1, // Moderate verbose logging
      standardFontDataUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@latest/standard_fonts/',
      ignoreErrors: ignoreErrors, // Try to render even with errors
      isEvalSupported: true,
      useSystemFonts: true,
      enableXfa: true, // Enable XFA form support
      useWorkerFetch: true,
      rangeChunkSize: 65536, // Increased chunk size
      maxImageSize: -1,      // No image size limit
      nativeImageDecoderSupport: 'all' // Force native image decoder
    });
    
    console.log("PDF.js loading task created for thumbnail");
    
    // Race against timeout
    const pdf = await Promise.race([loadingTask.promise, timeoutPromise]);
    console.log("PDF document loaded for thumbnail, pages:", pdf.numPages);
    
    // Get the requested page - default to first page if specified page doesn't exist
    const pageNum = (page > 0 && page <= pdf.numPages) ? page : 1;
    console.log(`Getting page ${pageNum} for thumbnail`);
    const pdfPage = await pdf.getPage(pageNum);
    
    // Calculate scale to fit within dimensions - using higher scale for better quality
    const viewport = pdfPage.getViewport({ scale: 1.0 });
    const qualityScale = useHighQualityRendering ? scale : 1.0;
    const displayScale = Math.min(width / viewport.width, height / viewport.height) * qualityScale;
    const scaledViewport = pdfPage.getViewport({ scale: displayScale });
    
    console.log(`Creating canvas with dimensions: ${scaledViewport.width}x${scaledViewport.height}`);
    
    // Create a canvas with specific settings for image-based PDFs
    const canvas = document.createElement('canvas');
    canvas.width = scaledViewport.width;
    canvas.height = scaledViewport.height;
    
    // Use a more reliable 2D context configuration specifically for images
    const context = canvas.getContext('2d', { 
      alpha: false,        // No transparency for better rendering
      desynchronized: false, // More reliable rendering
      willReadFrequently: true, // Optimize for reading pixel data
      imageSmoothingEnabled: true, // Enable image smoothing
      imageSmoothingQuality: 'high' // High quality image smoothing
    });
    
    // Fill with white background first to handle transparent PDFs
    context.fillStyle = 'white';
    context.fillRect(0, 0, canvas.width, canvas.height);
    
    console.log("Starting page render for thumbnail");
    
    // Render the page with better settings for image-based PDFs
    try {
      const renderOptions = {
        canvasContext: context,
        viewport: scaledViewport,
        intent: useHighQualityRendering ? 'print' : 'display', // Better quality for image-based PDFs
        renderInteractiveForms: true,
        enableWebGL: true,
        useRequestAnimationFrame: false, // Disable animation frame to ensure completion
        backgroundColorToReplace: [255, 255, 255] // Replace transparent with white
      };
      
      // More reliable rendering approach
      await pdfPage.render(renderOptions).promise;
      
      // Convert canvas to image at high quality
      const imageQuality = useHighQualityRendering ? 1.0 : 0.92;
      const dataUrl = canvas.toDataURL('image/png', imageQuality);
      
      // Test by creating an image from our data URL to verify it works
      await new Promise((resolve, reject) => {
        const testImg = new Image();
        testImg.onload = () => resolve();
        testImg.onerror = () => reject(new Error("Generated image is invalid"));
        testImg.src = dataUrl;
      });
      
      return dataUrl;
    } catch (renderErr) {
      console.error("Primary render approach failed:", renderErr);
      
      // Fallback: Try with simpler settings
      try {
        console.log("Attempting alternate rendering approach for image-based PDF");
        
        // Clear canvas and try again with simpler settings
        context.fillStyle = 'white';
        context.fillRect(0, 0, canvas.width, canvas.height);
        
        // Modified viewport with slightly different scale to trigger different code path
        const altViewport = pdfPage.getViewport({ scale: displayScale * 0.99 });
        
        // Simpler render options
        await pdfPage.render({
          canvasContext: context,
          viewport: altViewport,
          intent: 'display',
          renderInteractiveForms: false
        }).promise;
        
        return canvas.toDataURL('image/png', 0.92);
      } catch (fallbackErr) {
        console.error("Fallback rendering failed:", fallbackErr);
        
        // Last resort: Try direct image extraction for image-based PDFs
        try {
          console.log("Attempting direct image extraction for image-based PDF");
          
          // Get page operations that might contain images
          const opList = await pdfPage.getOperatorList();
          
          // If no operations, give up
          if (!opList || !opList.fnArray || opList.fnArray.length === 0) {
            throw new Error("No operations found in PDF page");
          }
          
          // Check if page has any images by looking for specific operation codes
          const hasImages = opList.fnArray.some(op => 
            op === pdfjs.OPS.paintImageXObject || 
            op === pdfjs.OPS.paintInlineImageXObject || 
            op === pdfjs.OPS.paintImageMaskXObject
          );
          
          if (hasImages) {
            console.log("Page contains images, attempting direct extraction");
            
            // Try to render at even higher quality for image-based PDFs
            context.fillStyle = 'white';
            context.fillRect(0, 0, canvas.width, canvas.height);
            
            // Create a larger viewport for highest possible quality
            const hiResViewport = pdfPage.getViewport({ scale: displayScale * 1.5 });
            const hiResCanvas = document.createElement('canvas');
            hiResCanvas.width = hiResViewport.width;
            hiResCanvas.height = hiResViewport.height;
            const hiResContext = hiResCanvas.getContext('2d', { 
              alpha: false, 
              imageSmoothingEnabled: true,
              imageSmoothingQuality: 'high'
            });
            
            hiResContext.fillStyle = 'white';
            hiResContext.fillRect(0, 0, hiResCanvas.width, hiResCanvas.height);
            
            // Attempt one more render with highest quality settings
            await pdfPage.render({
              canvasContext: hiResContext,
              viewport: hiResViewport,
              intent: 'print',
              enableWebGL: true,
              renderInteractiveForms: false
            }).promise;
            
            // Scale down to target size with high quality
            context.drawImage(hiResCanvas, 0, 0, canvas.width, canvas.height);
            
            return canvas.toDataURL('image/png', 1.0);
          } else {
            // No images found - create a text-based placeholder
            console.log("No images found in PDF page");
            throw new Error("No images found in PDF page");
          }
        } catch (imageExtractionErr) {
          console.error("Image extraction failed:", imageExtractionErr);
          
          // Create a basic error thumbnail if all else fails
          const errorThumbnail = createImageBasedPdfThumbnail(
            "Image-based PDF (Preview not available)"
          );
          return errorThumbnail;
        }
      }
    }
  } catch (err) {
    console.error("Error generating PDF thumbnail:", err);
    return generatePdfErrorThumbnail("PDF Preview Error");
  }
}

/**
 * Create a custom thumbnail for image-based PDFs
 */
function createImageBasedPdfThumbnail(message) {
  const canvas = document.createElement('canvas');
  canvas.width = 800;
  canvas.height = 1200;
  const ctx = canvas.getContext('2d');
  
  // Fill background
  ctx.fillStyle = '#f5f5f5';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  
  // Draw PDF icon
  const iconSize = 200;
  const iconX = (canvas.width - iconSize) / 2;
  const iconY = (canvas.height - iconSize) / 2 - 100;
  
  // PDF document shape
  ctx.fillStyle = '#E53935';
  ctx.fillRect(iconX, iconY, iconSize, iconSize);
  
  // PDF text
  ctx.fillStyle = 'white';
  ctx.font = 'bold 60px Arial';
  ctx.textAlign = 'center';
  ctx.fillText('PDF', iconX + iconSize / 2, iconY + iconSize / 2 + 10);
  
  // Image indicator
  ctx.fillStyle = 'white';
  ctx.font = 'bold 24px Arial';
  ctx.fillText('IMAGE', iconX + iconSize / 2, iconY + iconSize / 2 + 50);
  
  // Message
  ctx.fillStyle = '#333';
  ctx.font = '22px Arial';
  ctx.fillText(message, canvas.width / 2, canvas.height / 2 + 100);
  
  return canvas.toDataURL('image/png');
}

/**
 * Generate a simple placeholder thumbnail to show while the real one loads
 */
function generatePlaceholderThumbnail(message) {
  const svg = `
    <svg width="800" height="1200" xmlns="http://www.w3.org/2000/svg">
      <rect width="100%" height="100%" fill="#f5f5f5"/>
      <g transform="translate(400, 500)">
        <rect x="-100" y="-120" width="200" height="240" fill="#2196f3" rx="8" ry="8"/>
        <text x="0" y="-40" font-family="Arial" font-size="60" text-anchor="middle" fill="white">PDF</text>
        <rect x="-80" y="20" width="160" height="12" fill="white" rx="2" ry="2" opacity="0.8"/>
        <rect x="-80" y="50" width="160" height="12" fill="white" rx="2" ry="2" opacity="0.8"/>
        <rect x="-80" y="80" width="120" height="12" fill="white" rx="2" ry="2" opacity="0.8"/>
      </g>
      <text x="400" y="700" font-family="Arial" font-size="24" text-anchor="middle" fill="#333">${message}</text>
    </svg>
  `;
  
  // Convert the SVG to a data URL
  return `data:image/svg+xml;base64,${btoa(svg)}`;
}

/**
 * Generate an error thumbnail for PDFs with a more robust approach
 */
function generatePdfErrorThumbnail(message) {
  try {
    // Create an SVG with the PDF icon and error message
    const svg = `
      <svg width="800" height="1200" xmlns="http://www.w3.org/2000/svg">
        <rect width="100%" height="100%" fill="#f8f9fa"/>
        <g transform="translate(400, 500)">
          <rect x="-100" y="-120" width="200" height="240" fill="#e53935" rx="8" ry="8"/>
          <text x="0" y="-40" font-family="Arial" font-size="60" text-anchor="middle" fill="white">PDF</text>
          <rect x="-60" y="20" width="120" height="10" fill="white" rx="2" ry="2"/>
          <rect x="-60" y="50" width="120" height="10" fill="white" rx="2" ry="2"/>
        </g>
        <text x="400" y="700" font-family="Arial" font-size="24" text-anchor="middle" fill="#333">${message}</text>
      </svg>
    `;
    
    // Convert the SVG to a data URL
    return `data:image/svg+xml;base64,${btoa(svg)}`;
  } catch (err) {
    console.error("Error generating PDF error thumbnail:", err);
    // Ultra-fallback
    return `data:image/svg+xml;base64,${btoa('<svg width="400" height="600" xmlns="http://www.w3.org/2000/svg"><rect width="100%" height="100%" fill="#f0f0f0"/><text x="50%" y="50%" font-family="Arial" font-size="24" text-anchor="middle" fill="#333">PDF Error</text></svg>')}`;
  }
}

/**
 * Check if PDF contains binary data that can be rendered
 * @param {Object} file - File object to check
 * @returns {boolean} - Whether the file can be rendered
 */
function isPdfPreviewable(file) {
  return !!(file && (file.buffer || (file.dataUrl && file.dataUrl.startsWith('data:application/pdf'))));
}

/**
 * Prepares PDF files for submission to the Anthropic API
 * Handles proper base64 encoding of PDF files
 * 
 * @param {Array} files - Array of file objects from your FileUpload component
 * @returns {Array} - Array of properly formatted content blocks for the API
 */
function prepareFilesForAnthropicApi(files) {
  const contentBlocks = [];
  
  for (const file of files) {
    if (file.type === 'application/pdf' || 
        (file.name && file.name.toLowerCase().endsWith('.pdf'))) {
      
      console.log(`Processing PDF file: ${file.name} (${file.size} bytes)`);
      
      // Extract PDF data based on available properties
      let base64Data = '';
      
      try {
        if (file.dataUrl) {
          // If we have a dataUrl, extract the base64 data
          base64Data = file.dataUrl.split(',')[1];
          console.log("Using dataUrl for PDF content");
        } else if (file.buffer) {
          // If we have a buffer, convert it to base64 in a browser-compatible way
          try {
            // First check if it's already an ArrayBuffer or needs conversion
            const arrayBuffer = file.buffer instanceof ArrayBuffer 
              ? file.buffer 
              : (file.buffer.buffer || new Uint8Array(file.buffer).buffer);
            
            // Use browser's built-in btoa function with proper byte handling
            const binary = [];
            const bytes = new Uint8Array(arrayBuffer);
            const len = bytes.byteLength;
            
            // Convert binary data to base64
            // We need to do this in chunks to avoid "Maximum call stack size exceeded" errors
            const chunkSize = 1024;
            for (let i = 0; i < len; i += chunkSize) {
              const chunk = bytes.slice(i, Math.min(i + chunkSize, len));
              binary.push(String.fromCharCode.apply(null, chunk));
            }
            
            base64Data = btoa(binary.join(''));
            console.log("Using buffer for PDF content, converted to base64");
          } catch (err) {
            console.error("Error converting buffer to base64:", err);
            // Try alternative approach for very large files
            try {
              const bytes = new Uint8Array(file.buffer);
              let binary = '';
              for (let i = 0; i < bytes.byteLength; i++) {
                binary += String.fromCharCode(bytes[i]);
              }
              base64Data = btoa(binary);
              console.log("Used alternative base64 encoding approach");
            } catch (altErr) {
              console.error("All base64 encoding approaches failed:", altErr);
              continue;
            }
          }
        } else if (file.content && typeof file.content === 'string' && file.content.startsWith('data:application/pdf;base64,')) {
          // If we have content as a base64 data URL, extract the base64 part
          base64Data = file.content.split(',')[1];
          console.log("Using content data URL for PDF content");
        } else {
          console.error(`PDF file ${file.name} doesn't contain usable binary data`);
          continue;
        }
        
        // Create a document content block for this PDF
        contentBlocks.push({
          type: "document",
          source: {
            type: "base64",
            media_type: "application/pdf",
            data: base64Data
          }
        });
        
        console.log(`PDF ${file.name} prepared for API submission`);
      } catch (err) {
        console.error(`Error preparing PDF ${file.name} for API:`, err);
      }
    } else {
      console.log(`Skipping non-PDF file: ${file.name}`);
      // For other file types, you could implement different handling here
    }
  }
  
  return contentBlocks;
}

/**
 * Get the appropriate model name that supports PDF processing
 * @returns {string} - The model name to use for PDF processing
 */
function getPdfSupportedModel() {
  // Get the configured model or fall back to a model that supports PDFs
  const configuredModel = localStorage.getItem('anthropic_model') || '';
  
  // List of models that support PDF processing
  const pdfSupportedModels = [
    'claude-3-7-sonnet-20250219',
    'claude-3-5-sonnet-20241022',
    'claude-3-5-sonnet-20240620',
    'claude-3-5-haiku-20241022'
  ];
  
  // If the configured model supports PDFs, use it
  if (pdfSupportedModels.includes(configuredModel)) {
    return configuredModel;
  }
  
  // Otherwise, use the latest Sonnet model that supports PDFs
  return 'claude-3-7-sonnet-20250219';
}

/**
 * Check if we need to add the PDF beta header
 * @param {Array} files - Array of file objects
 * @returns {boolean} - True if we need to add the PDF beta header
 */
function needsPdfBetaHeader(files) {
  if (!files || !Array.isArray(files) || files.length === 0) {
    return false;
  }
  
  return files.some(file => 
    (file.type === 'application/pdf') || 
    (file.name && file.name.toLowerCase().endsWith('.pdf'))
  );
}

/**
 * Component integration function - call this to get a PDF viewer component
 * This function helps maintain the thumbnail throughout lifecycle events
 */
function createPdfViewerComponent(pdfFile, options = {}) {
  // Return an object with methods for the UI component to use
  return {
    async getThumbnail() {
      // First return a placeholder to ensure something appears immediately
      const placeholder = generatePlaceholderThumbnail("Loading PDF...");
      
      try {
        if (!pdfFile || !isPdfPreviewable(pdfFile)) {
          return generatePdfErrorThumbnail("Invalid PDF File");
        }
        
        // Choose the appropriate data source
        let pdfData;
        if (pdfFile.dataUrl) {
          pdfData = pdfFile.dataUrl;
        } else if (pdfFile.buffer) {
          pdfData = pdfFile.buffer;
        } else {
          return generatePdfErrorThumbnail("No PDF Data Available");
        }
        
        // Use enhanced thumbnail generation for image-based PDFs
        const thumbnail = await generatePDFThumbnail(pdfData, {
          ...options,
          useHighQualityRendering: true, // Always use high quality
          scale: 2.0, // Higher scale for better image quality
          fileName: pdfFile.name || "document.pdf"
        });
        
        // Store the thumbnail data URL in the file object for persistence
        if (pdfFile && thumbnail) {
          pdfFile._cachedThumbnail = thumbnail;
        }
        
        return thumbnail;
      } catch (err) {
        console.error("Error generating PDF viewer component:", err);
        return generatePdfErrorThumbnail("PDF Preview Failed");
      }
    },
    
    // If we already have a cached thumbnail, use it
    getCachedThumbnail() {
      if (pdfFile && pdfFile._cachedThumbnail) {
        return pdfFile._cachedThumbnail;
      }
      return generatePlaceholderThumbnail("Loading PDF...");
    }
  };
}

// Make sure all functions are available as named exports
export {
  extractTextFromPDF,
  generatePDFThumbnail,
  isPdfPreviewable,
  needsPdfBetaHeader,
  getPdfSupportedModel,
  prepareFilesForAnthropicApi,
  createPdfViewerComponent,
  // Helper functions are also exported to avoid "not defined" errors if referenced
  generatePlaceholderThumbnail,
  generatePdfErrorThumbnail,
  createImageBasedPdfThumbnail
};

// Keep the default export as well for backward compatibility
export default {
  extractTextFromPDF,
  generatePDFThumbnail,
  isPdfPreviewable,
  needsPdfBetaHeader,
  getPdfSupportedModel,
  prepareFilesForAnthropicApi,
  createPdfViewerComponent,
  generatePlaceholderThumbnail,
  generatePdfErrorThumbnail,
  createImageBasedPdfThumbnail
};